import type { StateAgency } from './types'

/**
 * Per-state reporting agency lookup. Used by getStatePack() to populate
 * `pack.agencies` for any state without a fully curated pack file.
 *
 * Each entry is verified from the agency's own .gov website. Phone numbers
 * are the public consumer-fraud / complaint intake line, not internal
 * switchboards. Source URLs (where the phone was confirmed) are recorded
 * in /docs/state-agencies-sources.md so we can re-verify on a cadence.
 *
 * RULES for editing this file:
 *   1. Every phone number must be verified on a state .gov page within the
 *      last 12 months. If you can't verify, delete the field — never guess.
 *   2. Order per state: AG consumer protection first, financial regulator
 *      second (only if they accept public fraud complaints), elder-fraud
 *      line third (only if it's a scam-focused line, NOT generic APS abuse
 *      reporting — those serve different distress paths).
 *   3. Description: one line, under 90 chars.
 *   4. Phone format: "1-XXX-XXX-XXXX".
 */

type StateAgencyEntry = {
  ag: StateAgency
  financial: StateAgency | null
  elder: StateAgency | null
}

export const STATE_AGENCIES: Record<string, StateAgencyEntry> = {
  AL: {
    ag: {
      name: 'Alabama Attorney General, Consumer Interest Division',
      description: 'File a complaint about a scam targeting Alabama residents.',
      url: 'https://www.alabamaag.gov/divisions/consumer-interest-division/',
      phone: '1-800-392-5658',
    },
    financial: {
      name: 'Alabama Securities Commission',
      description: 'Report investment fraud, unregistered brokers, or suspicious offers.',
      url: 'https://asc.alabama.gov/for-industry/enforcement/complaint-procedure/',
      phone: '1-800-222-1253',
    },
    elder: null,
  },
  AK: {
    ag: {
      name: 'Alaska Department of Law, Consumer Protection Unit',
      description: 'File a complaint about a scam targeting Alaska residents.',
      url: 'https://law.alaska.gov/department/civil/consumer/cpindex.html',
      phone: '1-888-576-2529',
    },
    financial: {
      name: 'Alaska Division of Banking and Securities',
      description: 'Report investment fraud or suspicious financial firms.',
      url: 'https://www.commerce.alaska.gov/web/dbs/',
      phone: '1-888-925-2521',
    },
    elder: null,
  },
  AR: {
    ag: {
      name: 'Arkansas Attorney General, Consumer Protection Division',
      description: 'File a complaint about a scam targeting Arkansas residents.',
      url: 'https://arkansasag.gov/divisions/public-protection/consumer-protection/',
      phone: '1-800-482-8982',
    },
    financial: {
      name: 'Arkansas Securities Department',
      description: 'Report investment fraud or unregistered securities offers.',
      url: 'https://securities.arkansas.gov/file-a-complaint-2/',
      phone: '1-800-981-4429',
    },
    elder: null,
  },
  CA: {
    ag: {
      name: 'California Attorney General, Public Inquiry Unit',
      description: 'File a consumer complaint about a scam targeting Californians.',
      url: 'https://oag.ca.gov/contact/consumer-complaint-against-business-or-company',
      phone: '1-800-952-5225',
    },
    financial: {
      name: 'California Department of Financial Protection and Innovation',
      description: 'Report investment fraud or financial-services scams.',
      url: 'https://dfpi.ca.gov/submit-a-complaint/',
      phone: '1-866-275-2677',
    },
    elder: null,
  },
  CO: {
    ag: {
      name: 'Colorado Attorney General, Consumer Protection Section',
      description: 'Report scams and consumer fraud through Stop Fraud Colorado.',
      url: 'https://stopfraudcolorado.gov/',
      phone: '1-800-222-4444',
    },
    financial: {
      name: 'Colorado Division of Securities',
      description: 'Report investment fraud or unregistered investment offers.',
      url: 'https://securities.colorado.gov/file-a-complaint',
      phone: '1-303-894-2320',
    },
    elder: null,
  },
  CT: {
    ag: {
      name: 'Connecticut Attorney General, Consumer Assistance Unit',
      description: 'File a complaint about a scam targeting Connecticut residents.',
      url: 'https://portal.ct.gov/AG/Common/Complaint-Form-Landing-page',
      phone: '1-860-808-5420',
    },
    financial: {
      name: 'Connecticut Department of Banking, Securities Division',
      description: 'Report investment fraud, broker misconduct, or unregistered offers.',
      url: 'https://portal.ct.gov/dob/consumer/consumer-complaints/securities-bd-ia',
      phone: '1-800-831-7225',
    },
    elder: null,
  },
  DE: {
    ag: {
      name: 'Delaware Department of Justice, Consumer Protection Unit',
      description: 'File a complaint about a scam targeting Delaware residents.',
      url: 'https://attorneygeneral.delaware.gov/fraud/cpu/',
      phone: '1-800-220-5424',
    },
    financial: {
      name: 'Delaware Investor Protection Unit',
      description: 'Report investment fraud and securities-related scams.',
      url: 'https://attorneygeneral.delaware.gov/fraud/ipu/',
      phone: '1-302-577-8424',
    },
    elder: null,
  },
  DC: {
    ag: {
      name: 'DC Office of the Attorney General, Office of Consumer Protection',
      description: 'File a complaint about a scam targeting DC residents.',
      url: 'https://oag.dc.gov/consumer-protection',
      phone: '1-202-442-9828',
    },
    financial: {
      name: 'DC Department of Insurance, Securities and Banking',
      description: 'Report investment fraud or financial-services scams in DC.',
      url: 'https://disb.dc.gov/service/file-complaint-or-report-fraud',
      phone: '1-202-727-8000',
    },
    elder: null,
  },
  FL: {
    ag: {
      name: 'Florida Attorney General, Consumer Protection Division',
      description: 'File a complaint about a scam targeting Florida residents.',
      url: 'https://www.myfloridalegal.com/consumer-protection',
      phone: '1-866-966-7226',
    },
    financial: {
      name: 'Florida Office of Financial Regulation',
      description: 'Report investment fraud or financial-services misconduct.',
      url: 'https://flofr.gov/enforcement/submit-a-complaint-or-tip',
      phone: '1-850-487-9687',
    },
    elder: {
      name: 'Seniors vs. Crime (Florida AG project)',
      description: 'Senior-focused scam reporting and consumer help line.',
      url: 'https://www.myfloridalegal.com/seniors-vs-crime',
      phone: '1-800-203-3099',
    },
  },
  GA: {
    ag: {
      name: "Georgia Attorney General's Consumer Protection Division",
      description: 'File a complaint about a scam targeting Georgia residents.',
      url: 'https://consumer.georgia.gov/',
      phone: '1-800-869-1123',
    },
    financial: {
      name: 'Georgia Secretary of State, Securities Division',
      description: 'Report investment fraud or unregistered securities offers.',
      url: 'https://sos.ga.gov/securities-division-georgia-secretary-states-office',
      phone: '1-470-312-2640',
    },
    elder: null,
  },
  HI: {
    ag: {
      name: 'Hawaii Office of Consumer Protection (DCCA)',
      description: 'File a complaint about a scam targeting Hawaii residents.',
      url: 'https://cca.hawaii.gov/ocp/consumer-complaint/',
      phone: '1-844-808-3222',
    },
    financial: {
      name: 'Hawaii Securities Enforcement Branch (DCCA)',
      description: 'Report investment fraud or unregistered securities offers.',
      url: 'https://cca.hawaii.gov/sec/complaints/',
      phone: '1-808-586-2740',
    },
    elder: null,
  },
  ID: {
    ag: {
      name: 'Idaho Attorney General, Consumer Protection Division',
      description: 'File a complaint about a scam targeting Idaho residents.',
      url: 'https://www.ag.idaho.gov/consumer-protection/consumer-complaints/',
      phone: '1-800-432-3545',
    },
    financial: {
      name: 'Idaho Department of Finance, Securities Bureau',
      description: 'Report investment fraud or unregistered securities offers.',
      url: 'https://www.finance.idaho.gov/securities-bureau/',
      phone: '1-888-346-3378',
    },
    elder: null,
  },
  IL: {
    ag: {
      name: 'Illinois Attorney General, Consumer Fraud Bureau',
      description: 'File a complaint about a scam targeting Illinois residents.',
      url: 'https://illinoisattorneygeneral.gov/file-a-complaint/consumer/',
      phone: '1-800-386-5438',
    },
    financial: {
      name: 'Illinois Securities Department (Secretary of State)',
      description: 'Report investment fraud or unregistered securities offers.',
      url: 'https://www.ilsos.gov/departments/securities/enforcement-complaint-center.html',
      phone: '1-800-628-7937',
    },
    elder: {
      name: 'Illinois Department on Aging, Senior HelpLine',
      description: 'Statewide line for older adults seeking help, including scam concerns.',
      url: 'https://ilaging.illinois.gov/resources/helpline-main.html',
      phone: '1-800-252-8966',
    },
  },
  IN: {
    ag: {
      name: 'Indiana Attorney General, Consumer Protection Division',
      description: 'File a complaint about a scam targeting Indiana residents.',
      url: 'https://www.in.gov/attorneygeneral/consumer-protection-division/',
      phone: '1-800-382-5516',
    },
    financial: {
      name: 'Indiana Securities Division (Secretary of State)',
      description: 'Report investment fraud or unregistered securities offers.',
      url: 'https://securities.sos.in.gov/',
      phone: '1-800-223-8791',
    },
    elder: null,
  },
  IA: {
    ag: {
      name: 'Iowa Attorney General, Consumer Protection Division',
      description: 'File a complaint about a scam targeting Iowa residents.',
      url: 'https://www.iowaattorneygeneral.gov/for-consumers/file-a-consumer-complaint',
      phone: '1-888-777-4590',
    },
    financial: {
      name: 'Iowa Insurance Division (Securities & Regulated Industries)',
      description: 'Report investment fraud or financial-services misconduct.',
      url: 'https://iid.iowa.gov/consumers/securities-regulated-businesses',
      phone: '1-877-955-1212',
    },
    elder: null,
  },
  KS: {
    ag: {
      name: 'Kansas Attorney General, Consumer Protection Division',
      description: 'File a complaint about a scam targeting Kansas residents.',
      url: 'https://www.ag.ks.gov/divisions/public-protection/consumer-protection',
      phone: '1-800-432-2310',
    },
    financial: {
      name: 'Kansas Securities Commissioner (Department of Insurance)',
      description: 'Report investment fraud or unregistered securities offers.',
      url: 'https://insurance.kansas.gov/securities/complaint/',
      phone: '1-800-432-2484',
    },
    elder: null,
  },
  KY: {
    ag: {
      name: 'Kentucky Attorney General, Office of Consumer Protection',
      description: 'File a complaint about a scam targeting Kentucky residents.',
      url: 'https://www.ag.ky.gov/about/Office-Divisions/OCP/Pages/default.aspx',
      phone: '1-888-432-9257',
    },
    financial: {
      name: 'Kentucky Department of Financial Institutions',
      description: 'Report investment fraud or financial-services misconduct.',
      url: 'https://kfi.ky.gov/',
      phone: '1-800-223-2579',
    },
    elder: null,
  },
  LA: {
    ag: {
      name: 'Louisiana Attorney General, Consumer Protection Section',
      description: 'State consumer protection hotline for fraud and scam complaints.',
      url: 'https://ag.state.la.us/ConsumerProtection',
      phone: '1-800-351-4889',
    },
    financial: {
      name: 'Louisiana Office of Financial Institutions',
      description: 'State regulator for securities and consumer finance fraud complaints.',
      url: 'https://ofi.la.gov/complaints/',
      phone: '1-888-525-9414',
    },
    elder: null,
  },
  ME: {
    ag: {
      name: 'Maine Attorney General, Consumer Protection Division',
      description: 'Files complaints and mediates consumer disputes statewide.',
      url: 'https://www.maine.gov/ag/consumer/',
      phone: '1-800-436-2131',
    },
    financial: {
      name: 'Maine Office of Securities',
      description: 'Investigates investment fraud and broker complaints.',
      url: 'https://www.maine.gov/pfr/securities/',
      phone: '1-877-624-8551',
    },
    elder: null,
  },
  MA: {
    ag: {
      name: 'Massachusetts Attorney General, Consumer Advocacy & Response Division',
      description: 'Handles consumer fraud complaints and provides mediation.',
      url: 'https://www.mass.gov/how-to/file-a-consumer-complaint',
      phone: '1-617-727-8400',
    },
    financial: {
      name: 'Massachusetts Securities Division',
      description: 'Secretary of the Commonwealth office handling investor fraud complaints.',
      url: 'https://www.sec.state.ma.us/divisions/securities/securities.htm',
      phone: '1-800-269-5428',
    },
    elder: {
      name: 'Massachusetts AG Elder Hotline',
      description: 'Statewide hotline for elder scams, exploitation, and consumer issues.',
      url: 'https://www.mass.gov/info-details/the-attorney-generals-elder-hotline',
      phone: '1-888-243-5337',
    },
  },
  MI: {
    ag: {
      name: 'Michigan Attorney General, Consumer Protection Team',
      description: 'Files complaints against businesses and investigates fraud.',
      url: 'https://www.michigan.gov/ag/consumer-protection',
      phone: '1-877-765-8388',
    },
    financial: {
      name: 'Michigan Department of Insurance and Financial Services',
      description: 'Investigates financial services fraud, including investments and lending.',
      url: 'https://www.michigan.gov/difs/consumers/complaint',
      phone: '1-877-999-6442',
    },
    elder: null,
  },
  MN: {
    ag: {
      name: 'Minnesota Attorney General, Consumer Action Division',
      description: 'Handles consumer fraud and deceptive business practice complaints.',
      url: 'https://www.ag.state.mn.us/consumer/',
      phone: '1-800-657-3787',
    },
    financial: {
      name: 'Minnesota Department of Commerce, Consumer Services',
      description: 'Investigates investment and financial fraud complaints.',
      url: 'https://mn.gov/commerce/money/fraud/',
      phone: '1-800-657-3602',
    },
    elder: null,
  },
  MS: {
    ag: {
      name: 'Mississippi Attorney General, Consumer Protection Division',
      description: 'Investigates deceptive trade practices and consumer fraud.',
      url: 'https://attorneygenerallynnfitch.com/divisions/consumer-protection/',
      phone: '1-800-281-4418',
    },
    financial: {
      name: 'Mississippi Secretary of State, Securities Division',
      description: 'Investigates investment fraud and securities violations.',
      url: 'https://www.sos.ms.gov/securities',
      phone: '1-601-359-1334',
    },
    elder: null,
  },
  MO: {
    ag: {
      name: 'Missouri Attorney General, Consumer Protection Division',
      description: 'Statewide hotline for fraud, scams, and consumer complaints.',
      url: 'https://ago.mo.gov/divisions/consumer/',
      phone: '1-800-392-8222',
    },
    financial: {
      name: 'Missouri Securities Division',
      description: 'Secretary of State investor hotline for investment fraud.',
      url: 'https://www.sos.mo.gov/securities',
      phone: '1-800-721-7996',
    },
    elder: null,
  },
  MT: {
    ag: {
      name: 'Montana Department of Justice, Office of Consumer Protection',
      description: 'Investigates unfair and deceptive business practices.',
      url: 'https://dojmt.gov/office-of-consumer-protection/',
      phone: '1-800-481-6896',
    },
    financial: {
      name: 'Montana Commissioner of Securities and Insurance',
      description: 'Investigates securities and insurance fraud complaints.',
      url: 'https://csimt.gov/file-a-complaint-2/',
      phone: '1-800-332-6148',
    },
    elder: null,
  },
  NE: {
    ag: {
      name: 'Nebraska Attorney General, Consumer Protection Division',
      description: 'Statewide consumer affairs hotline for fraud complaints.',
      url: 'https://protectthegoodlife.nebraska.gov/',
      phone: '1-800-727-6432',
    },
    financial: {
      name: 'Nebraska Department of Banking and Finance, Bureau of Securities',
      description: 'Investor hotline for investment fraud and broker complaints.',
      url: 'https://ndbf.nebraska.gov/consumers/complaints',
      phone: '1-877-471-3445',
    },
    elder: null,
  },
  NV: {
    ag: {
      name: 'Nevada Attorney General, Bureau of Consumer Protection',
      description: 'Investigates consumer fraud and deceptive trade practices.',
      url: 'https://ag.nv.gov/About/Consumer_Protection/Bureau_of_Consumer_Protection/',
      phone: '1-888-434-9989',
    },
    financial: {
      name: 'Nevada Secretary of State, Securities Division',
      description: 'Investigates investment fraud and securities violations.',
      url: 'https://www.nvsos.gov/sos/investor-information',
      phone: '1-702-486-2440',
    },
    elder: null,
  },
  NH: {
    ag: {
      name: 'New Hampshire DOJ, Consumer Protection & Antitrust Bureau',
      description: 'Statewide consumer fraud hotline run by the AG.',
      url: 'https://www.doj.nh.gov/consumer/',
      phone: '1-888-468-4454',
    },
    financial: {
      name: 'New Hampshire Bureau of Securities Regulation',
      description: 'Secretary of State office investigating investor fraud.',
      url: 'https://www.sos.nh.gov/securities-regulation',
      phone: '1-800-994-4200',
    },
    elder: null,
  },
  NJ: {
    ag: {
      name: 'New Jersey Division of Consumer Affairs',
      description: 'AG-housed division handling consumer fraud complaints.',
      url: 'https://www.njconsumeraffairs.gov/',
      phone: '1-800-242-5846',
    },
    financial: {
      name: 'New Jersey Bureau of Securities',
      description: 'Investigates investment fraud and broker misconduct.',
      url: 'https://www.njconsumeraffairs.gov/bos/Pages/contactus.aspx',
      phone: '1-866-446-8378',
    },
    elder: null,
  },
  NM: {
    ag: {
      name: 'New Mexico DOJ, Consumer Protection Division',
      description: 'Advocacy and Intervention Division handling fraud and scam complaints.',
      url: 'https://nmdoj.gov/about-the-office/consumer-affairs/',
      phone: '1-844-255-9210',
    },
    financial: {
      name: 'New Mexico Securities Division (Regulation & Licensing Department)',
      description: 'Investigates investment fraud and broker complaints.',
      url: 'https://www.rld.nm.gov/securities-division/',
      phone: '1-800-704-5533',
    },
    elder: null,
  },
  NC: {
    ag: {
      name: 'North Carolina DOJ, Consumer Protection Division',
      description: 'Statewide hotline for fraud, scams, and consumer complaints.',
      url: 'https://ncdoj.gov/protecting-consumers/',
      phone: '1-877-566-7226',
    },
    financial: {
      name: 'North Carolina Secretary of State, Securities Division',
      description: 'Investor hotline for investment fraud and broker verification.',
      url: 'https://www.sosnc.gov/divisions/securities',
      phone: '1-800-688-4507',
    },
    elder: null,
  },
  ND: {
    ag: {
      name: 'North Dakota Attorney General, Consumer Protection Division',
      description: 'Investigates deceptive trade practices and consumer fraud.',
      url: 'https://attorneygeneral.nd.gov/consumer-resources/',
      phone: '1-800-472-2600',
    },
    financial: {
      name: 'North Dakota Securities Department',
      description: 'Investigates investment fraud and broker complaints.',
      url: 'https://www.securities.nd.gov/',
      phone: '1-800-297-5124',
    },
    elder: null,
  },
  OH: {
    ag: {
      name: 'Ohio Attorney General, Consumer Protection Section',
      description: 'Statewide help center for consumer fraud and scam complaints.',
      url: 'https://www.ohioattorneygeneral.gov/Individuals-and-Families/Consumers/File-A-Complaint',
      phone: '1-800-282-0515',
    },
    financial: {
      name: 'Ohio Division of Securities (Department of Commerce)',
      description: 'Investor Protection Hotline for investment fraud.',
      url: 'https://com.ohio.gov/divisions-and-programs/securities',
      phone: '1-877-683-7841',
    },
    elder: null,
  },
  OK: {
    ag: {
      name: 'Oklahoma Attorney General, Consumer Protection Unit',
      description: 'Investigates consumer fraud and deceptive practices statewide.',
      url: 'https://www.oag.ok.gov/consumer-protection-unit',
      phone: '1-833-681-1895',
    },
    financial: {
      name: 'Oklahoma Department of Securities',
      description: 'Investigates investment fraud and securities violations.',
      url: 'https://securities.ok.gov/Enforcement/',
      phone: '1-405-280-7775',
    },
    elder: null,
  },
  OR: {
    ag: {
      name: 'Oregon Department of Justice, Consumer Hotline',
      description: 'File a complaint about a scam targeting Oregon residents.',
      url: 'https://www.doj.state.or.us/consumer-protection/',
      phone: '1-877-877-9392',
    },
    financial: {
      name: 'Oregon Division of Financial Regulation',
      description: 'Report investment fraud or unlicensed financial services.',
      url: 'https://dfr.oregon.gov/help/complaints-licenses/pages/file-complaint.aspx',
      phone: '1-888-877-4894',
    },
    elder: {
      name: 'Oregon SAFELINE',
      description: 'Report financial exploitation of seniors or vulnerable adults.',
      url: 'https://dfr.oregon.gov/financial/protect/pages/index.aspx',
      phone: '1-855-503-7233',
    },
  },
  PA: {
    ag: {
      name: 'Pennsylvania Bureau of Consumer Protection',
      description: 'File a consumer protection complaint with the PA Attorney General.',
      url: 'https://www.attorneygeneral.gov/submit-a-complaint/consumer-complaint/',
      phone: '1-800-441-2555',
    },
    financial: {
      name: 'PA Department of Banking and Securities',
      description: 'Report concerns about banks, lenders, and investment fraud.',
      url: 'https://www.dobs.pa.gov/Consumers/Pages/Contact.aspx',
      phone: '1-800-722-2657',
    },
    elder: {
      name: 'PA Attorney General Senior Protection Helpline',
      description: 'Dedicated helpline for older Pennsylvanians targeted by fraud.',
      url: 'https://www.attorneygeneral.gov/protect-yourself/seniors/',
      phone: '1-866-623-2137',
    },
  },
  RI: {
    ag: {
      name: 'Rhode Island Attorney General, Consumer Protection Unit',
      description: 'File a consumer complaint with the RI Attorney General.',
      url: 'https://riag.ri.gov/consumerprotection',
      phone: '1-401-274-4400',
    },
    financial: {
      name: 'RI Department of Business Regulation, Securities Division',
      description: 'Report securities fraud or unlicensed brokers in Rhode Island.',
      url: 'https://dbr.ri.gov/divisions/securities/securities.php',
      phone: '1-401-462-9586',
    },
    elder: null,
  },
  SC: {
    ag: {
      name: 'South Carolina Department of Consumer Affairs',
      description: 'File a complaint about scams or unfair business practices in SC.',
      url: 'https://consumer.sc.gov/consumer-resources/consumer-complaints',
      phone: '1-800-922-1594',
    },
    financial: {
      name: 'SC Attorney General, Securities Division',
      description: 'Report investment fraud or unregistered securities sales.',
      url: 'https://www.scag.gov/inside-the-office/legal-services-division/securities/enforcement/submit-a-complaint/',
      phone: '1-803-734-9916',
    },
    elder: null,
  },
  SD: {
    ag: {
      name: 'South Dakota Attorney General, Consumer Protection',
      description: 'File a consumer protection complaint with the SD Attorney General.',
      url: 'https://atg.sd.gov/complaintform.aspx',
      phone: '1-800-300-1986',
    },
    financial: {
      name: 'SD Division of Insurance, Securities Regulation',
      description: 'Report investment fraud or violations of SD securities law.',
      url: 'https://dlr.sd.gov/securities/complaints.aspx',
      phone: '1-605-773-3563',
    },
    elder: null,
  },
  TN: {
    ag: {
      name: 'Tennessee Division of Consumer Affairs',
      description: 'File a complaint about scams or unfair practices in Tennessee.',
      url: 'https://www.tn.gov/attorneygeneral/working-for-tennessee/consumer/file-a-complaint.html',
      phone: '1-615-741-4737',
    },
    financial: {
      name: 'TN Department of Commerce & Insurance, Securities Division',
      description: 'Report investment fraud or violations of TN securities law.',
      url: 'https://www.tn.gov/commerce/securities-division.html',
      phone: '1-800-863-9117',
    },
    elder: null,
  },
  TX: {
    ag: {
      name: 'Texas Attorney General, Consumer Protection Hotline',
      description: 'File a complaint about scams targeting Texas residents.',
      url: 'https://www.texasattorneygeneral.gov/consumer-protection/file-consumer-complaint',
      phone: '1-800-621-0508',
    },
    financial: {
      name: 'Texas State Securities Board',
      description: 'Report investment fraud or unlicensed securities dealers.',
      url: 'https://ssb.texas.gov/index.php/securities-professionals/enforcement/file-complaint',
      phone: '1-888-663-0009',
    },
    elder: null,
  },
  UT: {
    ag: {
      name: 'Utah Division of Consumer Protection',
      description: 'File a complaint about scams or unfair business practices in Utah.',
      url: 'https://dcp.utah.gov/i-have-a-problem/',
      phone: '1-801-530-6601',
    },
    financial: {
      name: 'Utah Division of Securities',
      description: 'Report investment fraud or unlicensed securities activity.',
      url: 'https://securities.utah.gov/enforcement/file-a-complaint/',
      phone: '1-801-530-6600',
    },
    elder: null,
  },
  VT: {
    ag: {
      name: 'Vermont Attorney General, Consumer Assistance Program',
      description: 'File a consumer complaint with the Vermont Attorney General.',
      url: 'https://ago.vermont.gov/cap',
      phone: '1-800-649-2424',
    },
    financial: {
      name: 'Vermont Department of Financial Regulation, Securities Division',
      description: 'Report investment fraud or unlicensed brokers in Vermont.',
      url: 'https://dfr.vermont.gov/consumers/file-complaint/securities',
      phone: '1-877-550-3907',
    },
    elder: null,
  },
  VA: {
    ag: {
      name: 'Virginia Attorney General, Consumer Protection',
      description: 'File a consumer protection complaint with the VA Attorney General.',
      url: 'https://www.oag.state.va.us/consumer-protection/',
      phone: '1-800-552-9963',
    },
    financial: {
      name: 'Virginia SCC, Division of Securities & Retail Franchising',
      description: 'Report investment fraud or unlicensed securities activity in VA.',
      url: 'https://www.scc.virginia.gov/consumers/consumer-investments/file-srf-complaint/',
      phone: '1-804-371-9051',
    },
    elder: null,
  },
  WA: {
    ag: {
      name: 'Washington Attorney General, Consumer Protection Division',
      description: 'File a consumer protection complaint with the WA Attorney General.',
      url: 'https://www.atg.wa.gov/file-complaint',
      phone: '1-800-551-4636',
    },
    financial: {
      name: 'WA Department of Financial Institutions, Securities Division',
      description: 'Report investment fraud or unregistered securities in Washington.',
      url: 'https://dfi.wa.gov/securities',
      phone: '1-877-746-4334',
    },
    elder: null,
  },
  WV: {
    ag: {
      name: 'West Virginia Attorney General, Consumer Protection Division',
      description: 'File a consumer protection complaint with the WV Attorney General.',
      url: 'https://ago.wv.gov/consumer-protection/file-complaint-consumer-protection-division',
      phone: '1-800-368-8808',
    },
    financial: {
      name: 'West Virginia Securities Commission',
      description: 'Report investment fraud or violations of WV securities law.',
      url: 'https://www.wvsao.gov/Securities/Default',
      phone: '1-877-982-9148',
    },
    elder: null,
  },
  WI: {
    ag: {
      name: 'Wisconsin Department of Agriculture, Trade and Consumer Protection',
      description: 'File a consumer complaint about scams targeting Wisconsin residents.',
      url: 'https://datcp.wi.gov/Pages/Programs_Services/ConsumerProtection.aspx',
      phone: '1-800-422-7128',
    },
    financial: {
      name: 'WI Department of Financial Institutions',
      description: 'Report investment fraud or unregistered securities in Wisconsin.',
      url: 'https://dfi.wi.gov/Pages/Securities/InvestorResources/FileAComplaint.aspx',
      phone: '1-800-452-3328',
    },
    elder: null,
  },
  WY: {
    ag: {
      name: 'Wyoming Attorney General, Consumer Protection Unit',
      description: 'File a consumer protection complaint with the WY Attorney General.',
      url: 'https://ag.wyo.gov/law-office-division/consumer-protection-and-antitrust-unit/consumer-complaints',
      phone: '1-307-777-6397',
    },
    financial: {
      name: 'Wyoming Secretary of State, Compliance Division',
      description: 'Report investment fraud or unregistered securities in Wyoming.',
      url: 'https://sos.wyo.gov/Investing/AvoidBecomingAFinancialVictim.aspx',
      phone: '1-307-777-7370',
    },
    elder: null,
  },
}

/**
 * Returns the per-state agency entries as a StateAgency[] for merging into
 * a state pack. Order: AG → financial regulator → elder line. Nulls
 * filtered out.
 */
export function getStateAgencies(code: string): StateAgency[] {
  const entry = STATE_AGENCIES[code.toUpperCase()]
  if (!entry) return []
  return [entry.ag, entry.financial, entry.elder].filter(
    (a): a is StateAgency => a !== null,
  )
}
